import { useState, useEffect, useRef } from "react";
import { apiUrl } from "../constants/constants";
import Cookie from "js-cookie";

interface Message {
  user: string;
  text: string;
  isMarkdown?: boolean;
}

interface StreamingOptions {
  question: string;
  thread_id: string;
  content_url: string | null;
  setHistory: React.Dispatch<
    React.SetStateAction<{ [key: string]: Message[] }>
  >;
  threadId: number;
}

const useStreaming = () => {
  const [messages, setMessages] = useState<string[]>([]);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const startStreaming = async (options: StreamingOptions) => {
    if (isStreaming) return;

    setIsStreaming(true);
    setMessages([]);
    let finalResponse = "";
    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(apiUrl + "/chat/real", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          question: options?.question,
          thread_id: options?.thread_id,
          content_url: options?.content_url,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(response.statusText);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n").filter((line) => line.trim());

        for (const line of lines) {
          try {
            if (line === "data: [DONE]") continue;
            if (!line.startsWith("data: ")) continue;

            const jsonStr = line.replace("data: ", "");
            const jsonData = JSON.parse(jsonStr);

            if (jsonData.content) {
              const newResponse = finalResponse + jsonData.content;
              finalResponse = newResponse;
              setMessages((prev) => [...prev, newResponse]);
            } else if (jsonData.error) {
              const errorResponse = jsonData.error;
              finalResponse = errorResponse;

              setMessages((prev) => [...prev, errorResponse]);
              break;
            }
          } catch (error) {
            console.error("Error parsing chunk:", error);
          }
        }
      }

      // Update history after streaming is complete with the final response
      if (finalResponse) {
        options.setHistory((prev) => {
          const existingMessages = prev[options.threadId] || [];
          // Filter out any incomplete messages
          const filteredMessages = existingMessages.filter(
            (msg) => !(msg.user === "ChatGPT" && !msg.text)
          );
          Cookie.set(
            `history`,
            JSON.stringify({
              ...prev,
              [options.threadId]: [
                ...filteredMessages,
                { user: "You", text: options.question },
                { user: "ChatGPT", text: finalResponse },
              ],
            }),
            {
              expires: 365,
            }
          );
          return {
            ...prev,
            [options.threadId]: [
              ...filteredMessages,
              { user: "You", text: options.question },
              { user: "ChatGPT", text: finalResponse },
            ],
          };
        });
      }
    } catch (error: any) {
      if (error.name === "AbortError") {
        console.log("Streaming was cancelled");
      } else {
        console.error("Streaming error:", error);
        throw error;
      }
    } finally {
      setIsStreaming(false);
    }
  };

  const stopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
  };

  useEffect(() => {
    return () => stopStreaming(); // Cleanup on unmount
  }, []);

  return { messages, isStreaming, startStreaming, stopStreaming };
};

export default useStreaming;
